import React from 'react';
import { Button } from '@/components/ui/button';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Card, CardContent } from '@/components/ui/card';
import { Link } from 'react-router-dom';
import { Download, Users, Award, Target, MapPin, Globe, Code, Palette } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { Badge } from '@/components/ui/badge';
import { Helmet } from 'react-helmet-async';

const About = () => {
  const teamMembers = [
    {
      name: 'Fatima',
      role: 'Founder & Managing Director',
      image: './fatima.jpg',
      bio: 'Leads the company, its client relationships and communications from Doha. Brings a background in strategic communications, research and media production, with experience at Northwestern University in Qatar and Weber Shandwick.',
      badges: ['Strategic Communications', 'Research', 'Content Strategy'],
    },
    {
      name: 'Zain',
      role: 'Technical Lead',
      image: './zain.jpg',
      bio: 'Creative and technical leader with expertise across design and engineering. Leads product vision, system architecture, and drives innovation to deliver impactful solutions for clients.',
      badges: ['Full-Stack Development', 'AI Integration', 'System Architecture'],
    },
    {
      name: 'Ayesha',
      role: 'Marketing Strategist',
      image: './ayesha.jpg',
      bio: 'Responsible for marketing and business development. Ayesha is a strategic thinker with a knack for identifying market opportunities and crafting compelling narratives that resonate with target audiences.',
    }
  ];

  return (
    <>
      <Helmet>
        <title>About Us | Omnicraft Services - Meet Our Team & Vision</title>
        <meta name="description" content="Learn about Omnicraft Services, our team, mission, vision, and values. Discover our expertise in web, branding, and business solutions." />
        <link rel="icon" type="image/png" href="/logo.png" />
        <link rel="apple-touch-icon" href="/logo.png" />
      </Helmet>
      <div className="min-h-screen pt-16">
        {/* Hero Section */}
        <section className="section-padding bg-gradient-to-b from-primary/10 to-transparent dark:from-primary/5">
          <div className="container-custom">
            <div className="text-center max-w-3xl mx-auto">
              <h1 className="mb-6">About Omnicraft Services</h1>
              <p className="text-lg md:text-xl text-slate-700 dark:text-slate-300 mb-8">
                We're a dynamic team of professionals based in Doha, Qatar, dedicated to delivering comprehensive business solutions to clients across the Middle East, the USA and beyond. Our mission is to empower your success through innovative strategies, creative designs, and technical excellence.
              </p>
              <div className="flex justify-center">
                <AnimatedButton 
                  variant="primary"
                  onClick={() => window.location.href = '/contact'}
                >
                  Get in Touch
                </AnimatedButton>
              </div>
            </div>
          </div>
        </section>
        {/* Team Section */}
        <section className="section-padding">
          <div className="container-custom">
            <div className="text-center mb-12">
              <h2 className="mb-4">Meet Our Team</h2>
              <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
                Our diverse team of professionals brings together expertise from various fields to provide comprehensive solutions for you and your small business needs.
              </p>
            </div>
            
            {/* Flex rather than grid so a partial final row centres itself —
                a 3-col grid leaves 2 members hugging the left edge. */}
            <div className="flex flex-wrap justify-center gap-8">
              {teamMembers.map((member, index) => (
                <Card
                  key={index}
                  className="hover-card overflow-hidden w-full md:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.334rem)]"
                >
                  <div className="aspect-square overflow-hidden">
                    <img 
                      src={member.image} 
                      alt={member.name} 
                      className="w-full h-full object-cover" 
                    />
                  </div>
                  <CardContent className="pt-6">
                    <h3 className="text-xl font-semibold mb-1">{member.name}</h3>
                    <p className="text-primary font-medium mb-3">{member.role}</p>
                    <p className="text-slate-600 dark:text-slate-400">{member.bio}</p>
                    {member.badges && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {member.badges.map((badge) => (
                          <Badge key={badge} variant="secondary">{badge}</Badge>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
        {/* Mission & Vision */}
        <section className="section-padding bg-slate-50 dark:bg-slate-900/50">
          <div className="container-custom">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <Card className="hover-card">
                <CardContent className="pt-6">
                  <div className="text-center mb-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Target className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="text-xl font-bold mb-2">Our Mission</h3>
                  </div>
                  <p className="text-slate-600 dark:text-slate-400">
                    To provide businesses and individuals with comprehensive services that solve problems, enhance efficiency, and drive growth. We're committed to delivering excellence in every project we undertake.
                  </p>
                </CardContent>
              </Card>
              
              <Card className="hover-card">
                <CardContent className="pt-6">
                  <div className="text-center mb-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Award className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="text-xl font-bold mb-2">Our Vision</h3>
                  </div>
                  <p className="text-slate-600 dark:text-slate-400">
                    To become the go-to multi-service provider for businesses seeking comprehensive solutions under one roof. We aim to simplify complexity and enable our clients to focus on what they do best.
                  </p>
                </CardContent>
              </Card>
              
              <Card className="hover-card">
                <CardContent className="pt-6">
                  <div className="text-center mb-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Users className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="text-xl font-bold mb-2">Our Values</h3>
                  </div>
                  <ul className="space-y-2 text-slate-600 dark:text-slate-400">
                    <li>• Excellence in everything we do</li>
                    <li>• Innovation and continuous improvement</li>
                    <li>• Integrity and transparency</li>
                    <li>• Client satisfaction</li>
                    <li>• Collaborative approach</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>



        {/* CV Section */}
        {/* <section className="section-padding bg-slate-50 dark:bg-slate-900/50">
          <div className="container-custom">
                <div className="text-center mb-12">
              <h2 className="mb-4">Professional Experience</h2>
              <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
                Learn more about Zain's experience and qualifications, including his background and technical expertise.
              </p>
            </div>
            
            <div className="max-w-2xl mx-auto">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <h3 className="text-2xl font-bold">Zain</h3>
                      <p className="text-primary">Technical Lead, Omnicraft Services</p>
                    </div>
                    <Button variant="outline" className="flex items-center">
                      <Download className="mr-2 h-4 w-4" /> Download CV
                    </Button>
                  </div>
                  
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-lg font-semibold mb-2">Professional Summary</h4>
                      <p className="text-slate-600 dark:text-slate-400">
                        Experienced engineer with a proven track record in developing innovative solutions across multiple industries. As technical lead at Omnicraft Services, I focus on creating scalable, efficient systems that leverage cutting-edge technologies to solve complex business challenges.
                      </p>
                    </div>
                    
                    <div>
                      <h4 className="text-lg font-semibold mb-2">Key Skills</h4>
                      <div className="flex flex-wrap gap-2">
                        {[
                          'Software Development', 'AI Integration', 'Business Strategy',
                          'System Architecture', 'Tech Leadership', 'Project Management',
                          'Cloud Infrastructure', 'API Development', 'Database Design',
                        ].map((skill, index) => (
                          <span key={index} className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="text-lg font-semibold mb-2">Notable Projects</h4>
                      <ul className="space-y-3 text-slate-600 dark:text-slate-400">
                        <li className="flex items-start">
                          <span className="text-primary mr-2">•</span>
                          <div>
                            <strong>Learn Kogit</strong> - Led the development of an educational platform with comprehensive learning resources and AI-powered personalization.
                          </div>
                        </li>
                        <li className="flex items-start">
                          <span className="text-primary mr-2">•</span>
                          <div>
                            <strong>Ledger Tree Financial Group</strong> - Architected a secure, compliant financial services platform with integrated payment processing and reporting systems.
                          </div>
                        </li>
                        <li className="flex items-start">
                          <span className="text-primary mr-2">•</span>
                          <div>
                            <strong>AI Integration Project</strong> - Developed and implemented custom AI solutions for business process automation, reducing operational costs by 35%.
                          </div>
                        </li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section> */}
      </div>
    </>
  );
};

export default About;
